import {
  EFaceDataBaseType,
  EWallFaceLayout,
  IBaseDataEdgeFace,
  IBaseDataHoleFace,
  IFaceData,
  IFaceHolePoint
} from '../../data/face/face';
import SeparateWallStore from '../SeparateWallStore';
import { StructStore } from '../StructStore';
import WallStore from '../WallStore';
import { EEntityType, EFaceSide, IWallEdge } from '@/editor/types';
import { EColumnType, IColumnData, IWindowData } from '@/editor/data/model/model';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores, TGlobalStores } from '..';
import { getUniqueId } from '@/editor/utils';
import { EFaceType } from '@/editor/entities/type';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { getFaceWindowHoles } from '@/editor/utils/face';
import { HALF_GLASS_WALL_SEGMENT_HEIGHT } from '@/editor/constant/wall';
import { clamp } from 'lodash-es';
import { BaseFaceStore } from '../base/baseFaceStore';
import { IMaterialInfo } from '@/editor/data/material/material';
import { COLUMN_RENDER_OFFSET } from '@/editor/constant/column';

const COLUMN_EDGE_SIDES = [EFaceSide.MainLeftSide, EFaceSide.MainRightSide, EFaceSide.StartSide, EFaceSide.EndSide];

const COLUMN_CONTOUR_SIDES = [EFaceSide.TopSide, EFaceSide.BottomSide];

export class FaceStore {
  private _wallFaceDataMap: BaseFaceStore;
  private _sepWallFaceDataMap: BaseFaceStore;
  private _columnFaceDataMap: BaseFaceStore;
  private _holeFaceDataMap: BaseFaceStore;

  private _wallStore: WallStore;
  private _separateWallStore: SeparateWallStore;
  private _structStore: StructStore;

  constructor(gs: TGlobalStores) {
    this._holeFaceDataMap = new BaseFaceStore(gs);
    this._wallFaceDataMap = new BaseFaceStore(gs);
    this._sepWallFaceDataMap = new BaseFaceStore(gs);
    this._columnFaceDataMap = new BaseFaceStore(gs);

    this._wallStore = gs.wallStore;
    this._separateWallStore = gs.separateWallStore;
    this._structStore = gs.structStore;
  }

  // ======================== 实体墙相关 ========================
  getWallFaceDatas() {
    return this._wallFaceDataMap.getList();
  }

  // 获取墙面关联关系到id的映射
  getWallFaceRelativeMap() {
    const map = new Map<string, string>();
    for (const data of this.getWallFaceDatas()) {
      const wallId = data.relativeInfo.wallId;
      const side = data.relativeInfo.side;
      const startId = data.relativeInfo.startId;
      const endId = data.relativeInfo.endId;

      let key;
      if (side === EFaceSide.TopSide || side === EFaceSide.BottomSide) {
        key = `${wallId}-${side}`;
      } else if (startId && endId) {
        key = `${wallId}-${startId}-${endId}`;
      } else {
        continue;
      }
      map.set(key, data.id);
    }
    return map;
  }

  addWallFaceData(wallFaceData: Omit<IFaceData, 'id'> & { id?: string }) {
    const id = getUniqueId();
    const data = { ...wallFaceData, id };
    this._wallFaceDataMap.add(data);

    return data;
  }

  removeWallFaceData(wallFaceId: string) {
    this._wallFaceDataMap.remove(wallFaceId);
  }

  // ======================== 隔墙相关 ========================
  getSepWallFaceDatas() {
    return this._sepWallFaceDataMap.getList();
  }

  // 获取墙面关联关系到id的映射
  getSepWallFaceRelativeMap() {
    const map = new Map<string, string>();
    for (const data of this.getSepWallFaceDatas()) {
      const sepId = data.relativeInfo.sepId;
      const startId = data.relativeInfo.startId;
      const endId = data.relativeInfo.endId;
      const sepSide = data.relativeInfo.sepSide;
      const layout = data.relativeInfo.layout;

      let key;
      if (sepSide === EFaceSide.TopSide || sepSide === EFaceSide.BottomSide) {
        key = `${sepId}-${sepSide}`;
      } else if (startId && endId) {
        key = `${sepId}-${startId}-${endId}`;
        if (layout && layout !== EWallFaceLayout.ALL) key += `-${layout}`;
      } else {
        continue;
      }
      map.set(key, data.id);
    }
    return map;
  }

  addSepWallFaceData(sepWallFaceData: Omit<IFaceData, 'id'> & { id?: string }) {
    const id = getUniqueId();
    const data = { ...sepWallFaceData, id };
    this._sepWallFaceDataMap.add(data);

    return data;
  }

  updateSepWallMaterial(sepWallFaceId: string, materialInfo: IMaterialInfo) {
    this._sepWallFaceDataMap.updateMaterial(sepWallFaceId, materialInfo);
  }

  removeSepWallFaceData(sepWallFaceId: string) {
    this._sepWallFaceDataMap.remove(sepWallFaceId);
  }

  // ======================== 孔洞相关 ========================
  getHoleFaceDatas() {
    return this._holeFaceDataMap.getList();
  }

  getHoleFaceData(faceId: string) {
    return this._holeFaceDataMap.get(faceId);
  }

  addHoleFaceData(faceData: Omit<IFaceData, 'id'> & { id?: string }) {
    const id = getUniqueId();
    const data = { ...faceData, id };
    this._holeFaceDataMap.add(data);

    return data;
  }

  getHoleFaceMap() {
    const map = new Map<string, IFaceData>();
    const holeFaceDatas = this.getHoleFaceDatas();
    for (const data of holeFaceDatas) {
      const key = data.relativeInfo.dwId;
      if (!key) continue;
      map.set(key, data);
    }
    return map;
  }

  removeHoleFaceData(holeFaceId: string) {
    this._holeFaceDataMap.remove(holeFaceId);
  }

  // ======================== 柱子相关 ========================
  getColumnFaceDatas() {
    return this._columnFaceDataMap.getList();
  }

  getColumnFaceDataMap() {
    const map = new Map<string, string>();
    for (const data of this.getColumnFaceDatas()) {
      const key = `${data.relativeInfo.columnId}-${data.relativeInfo.columnSide}`;
      map.set(key, data.id);
    }
    return map;
  }

  addColumnFaceData(columnFaceData: Omit<IFaceData, 'id'> & { id?: string }) {
    const id = getUniqueId();
    const data = { ...columnFaceData, id };
    this._columnFaceDataMap.add(data);

    return data;
  }

  removeColumnFaceData(columnFaceId: string) {
    this._columnFaceDataMap.remove(columnFaceId);
  }

  // updateColumnFaceData(columnFaceId: string, columnFaceData: Partial<IFaceData>) {
  //   this._columnFaceDataMap.update(columnFaceId, columnFaceData);
  // }

  getColumnFaceData(faceId: string) {
    return this._columnFaceDataMap.get(faceId);
  }

  // ======================== 刷新逻辑 ========================
  // 更新实体墙墙面
  refreshWallFaceDatas() {
    const wallEdges = globalStores.edgeStore.getWallEdges();
    const wallFaceRelativeMap = this.getWallFaceRelativeMap();
    const wallWindowCache = new Map<string, IWindowData[]>();

    // 处理墙体边缘
    for (const wallEdge of wallEdges) {
      const wallId = wallEdge.wallId;
      const startId = wallEdge.startId;
      const endId = wallEdge.endId;
      const relativeKey = `${wallId}-${startId}-${endId}`;
      const relativeFaceDataId = wallFaceRelativeMap.get(relativeKey);
      const wallData = this._wallStore.getWallData(wallId);
      if (!wallData) continue;

      const baseData: IBaseDataEdgeFace & { side?: EFaceSide; center?: IPoint2d; clockwise?: boolean } = {
        side: wallEdge.side,
        start: wallEdge.start,
        end: wallEdge.end,
        height: wallData.baseData.height,
        offGround: wallData.baseData.offGround
      };
      if (wallEdge.center) {
        baseData.center = wallEdge.center;
        baseData.clockwise = wallEdge.clockwise;
      }

      // 添加挖孔数据
      const holes = [];
      if (wallEdge.side === EFaceSide.MainLeftSide || wallEdge.side === EFaceSide.MainRightSide) {
        if (wallWindowCache.has(wallId)) {
          const cacheWallWindowData = wallWindowCache.get(wallId);
          if (cacheWallWindowData) holes.push(...getFaceWindowHoles(cacheWallWindowData, baseData));
        } else {
          const windows = globalStores.modelStore.getWindowsByWallId(wallId) || [];
          wallWindowCache.set(wallId, windows);
          holes.push(...getFaceWindowHoles(windows, baseData));
        }
      }

      const relativeInfo = { wallId, side: wallEdge.side, startId, endId };
      if (relativeFaceDataId) {
        this._wallFaceDataMap.updateExtra(relativeFaceDataId, {
          baseData,
          holes,
          relativeInfo
        });
      } else {
        this.addWallFaceData({
          type: EFaceType.WallFace,
          baseDataType: baseData.center ? EFaceDataBaseType.ArcEdge : EFaceDataBaseType.Edge,
          baseData,
          relativeInfo,
          holes,
          materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.WallFace })
        });
      }

      wallFaceRelativeMap.delete(relativeKey);
    }

    // 处理墙体顶部底部
    const wallDatas = this._wallStore.getWallDatas();
    for (const wallData of wallDatas) {
      const topRelativeKey = `${wallData.id}-${EFaceSide.TopSide}`;
      const bottomRelativeKey = `${wallData.id}-${EFaceSide.BottomSide}`;
      const topRelativeFaceId = wallFaceRelativeMap.get(topRelativeKey);
      const bottomRelativeFaceId = wallFaceRelativeMap.get(bottomRelativeKey);

      const topRelativeInfo = {
        wallId: wallData.id,
        side: EFaceSide.TopSide
      };
      // 关联 id 取不到数据说明映射已失效，按新增处理
      const topRelativeFaceData = topRelativeFaceId ? this.getFaceData(topRelativeFaceId) : undefined;
      if (topRelativeFaceData) {
        this._wallFaceDataMap.updateExtra(topRelativeFaceData.id, {
          baseData: {
            ...topRelativeFaceData.baseData,
            contour: wallData.drawWallInfo.drawShape[0],
            offGround: wallData.baseData.height + wallData.baseData.offGround
          },
          holes: [],
          relativeInfo: topRelativeInfo
        });
      } else {
        this.addWallFaceData({
          type: EFaceType.WallFace,
          holes: [],
          baseDataType: EFaceDataBaseType.Contour,
          baseData: {
            contour: wallData.drawWallInfo.drawShape[0],
            offGround: wallData.baseData.height + wallData.baseData.offGround
          },
          relativeInfo: topRelativeInfo,
          materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.WallFace })
        });
      }

      const bottomRelativeInfo = {
        wallId: wallData.id,
        side: EFaceSide.BottomSide
      };
      const bottomRelativeFaceData = bottomRelativeFaceId ? this.getFaceData(bottomRelativeFaceId) : undefined;
      if (bottomRelativeFaceData) {
        this._wallFaceDataMap.updateExtra(bottomRelativeFaceData.id, {
          baseData: {
            ...bottomRelativeFaceData.baseData,
            contour: wallData.drawWallInfo.drawShape[0],
            offGround: wallData.baseData.offGround
          },
          holes: [],
          relativeInfo: bottomRelativeInfo
        });
      } else {
        this.addWallFaceData({
          type: EFaceType.WallFace,
          holes: [],
          baseDataType: EFaceDataBaseType.Contour,
          baseData: {
            contour: wallData.drawWallInfo.drawShape[0],
            offGround: wallData.baseData.offGround
          },
          relativeInfo: bottomRelativeInfo,
          materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.WallFace })
        });
      }

      wallFaceRelativeMap.delete(topRelativeKey);
      wallFaceRelativeMap.delete(bottomRelativeKey);
    }

    Array.from(wallFaceRelativeMap.values()).forEach((fdId) => this.removeWallFaceData(fdId));
  }

  // 更新隔墙墙面
  refreshSeparateWallFaceDatas() {
    const roomDatas = globalStores.roomStore.getRoomDatas();
    const sepWallEdges = roomDatas.flatMap((roomData) => globalStores.edgeStore.getSepEdges(roomData.id));
    const sepWallFaceRelativeMap = this.getSepWallFaceRelativeMap();
    const removeSepWallFaceIds = new Set(this.getSepWallFaceDatas().map((fd) => fd.id));
    const wallWindowMap = new Map<string, IWindowData[]>(); // 缓存优化

    // 处理隔墙墙体边缘
    for (const wallEdge of sepWallEdges) {
      // 虚拟隔墙跳过
      if (wallEdge.sepType === ESeparateWallType.VirtualWall) continue;
      // 隔墙边必然带 sepId，缺失说明该边不是隔墙边，跳过
      if (!wallEdge.sepId) continue;

      const wallId = wallEdge.wallId;
      const sepId = wallEdge.sepId;
      const startId = wallEdge.startId;
      const endId = wallEdge.endId;
      const relativeKey = `${sepId}-${startId}-${endId}`;

      // 处理半玻墙体
      if (wallEdge.sepType === ESeparateWallType.HalfGlassWall) {
        for (const layout of [EWallFaceLayout.DOWN, EWallFaceLayout.MIDDLE, EWallFaceLayout.UP]) {
          const _relativeKey = `${sepId}-${startId}-${endId}-${layout}`;
          const _relativeFaceId = this._refreshSepEdgeFaceData({
            relativeKey: _relativeKey,
            relativeMap: sepWallFaceRelativeMap,
            sepId,
            wallId,
            edge: wallEdge,
            layout,
            wallWindowMap
          });
          if (_relativeFaceId) removeSepWallFaceIds.delete(_relativeFaceId);
        }
        continue;
      }

      // 处理其他墙体
      const relativeFaceId = this._refreshSepEdgeFaceData({
        relativeKey,
        relativeMap: sepWallFaceRelativeMap,
        sepId,
        wallId,
        edge: wallEdge,
        wallWindowMap
      });
      if (relativeFaceId) removeSepWallFaceIds.delete(relativeFaceId);
    }

    // 处理墙体顶部底部
    const sepWallDatas = this._separateWallStore.getSepDatas();
    for (const sepWallData of sepWallDatas) {
      if (
        sepWallData.type === ESeparateWallType.BaseVirtualWall ||
        sepWallData.type === ESeparateWallType.VirtualWall
      ) {
        continue;
      }

      const topRelativeKey = `${sepWallData.id}-${EFaceSide.TopSide}`;
      const bottomRelativeKey = `${sepWallData.id}-${EFaceSide.BottomSide}`;
      const topRelativeFaceId = sepWallFaceRelativeMap.get(topRelativeKey);
      const bottomRelativeFaceId = sepWallFaceRelativeMap.get(bottomRelativeKey);
      const topBaseData = {
        contour: sepWallData.drawSeparateWallInfo.drawShape,
        offGround: sepWallData.height + sepWallData.offGround
      };
      const bottomBaseData = {
        contour: sepWallData.drawSeparateWallInfo.drawShape,
        offGround: sepWallData.offGround
      };
      const materialInfo = globalStores.getDefaultMaterial({
        type: EFaceType.SepWallFace,
        sepWallData
      });

      // 顶面处理
      const topRelativeInfo = {
        sepId: sepWallData.id,
        sepSide: EFaceSide.TopSide,
        side: EFaceSide.TopSide
      };
      if (topRelativeFaceId) {
        removeSepWallFaceIds.delete(topRelativeFaceId);
        this._sepWallFaceDataMap.updateExtra(topRelativeFaceId, {
          baseData: topBaseData,
          holes: [],
          relativeInfo: topRelativeInfo
        });
      } else {
        this.addSepWallFaceData({
          type: EFaceType.SepWallFace,
          holes: [],
          baseDataType: EFaceDataBaseType.Contour,
          baseData: topBaseData,
          relativeInfo: topRelativeInfo,
          materialInfo
        });
      }

      // 底面处理
      const bottomRelativeInfo = {
        sepId: sepWallData.id,
        sepSide: EFaceSide.BottomSide,
        side: EFaceSide.BottomSide
      };
      if (bottomRelativeFaceId) {
        removeSepWallFaceIds.delete(bottomRelativeFaceId);
        this._sepWallFaceDataMap.updateExtra(bottomRelativeFaceId, {
          baseData: bottomBaseData,
          holes: [],
          relativeInfo: bottomRelativeInfo
        });
      } else {
        this.addSepWallFaceData({
          type: EFaceType.SepWallFace,
          holes: [],
          baseDataType: EFaceDataBaseType.Contour,
          baseData: bottomBaseData,
          relativeInfo: bottomRelativeInfo,
          materialInfo
        });
      }
    }

    removeSepWallFaceIds.forEach((fdId) => this.removeSepWallFaceData(fdId));
  }

  // 刷新一面隔墙墙面
  private _refreshSepEdgeFaceData({
    relativeKey,
    relativeMap,
    sepId,
    wallId,
    edge,
    layout,
    wallWindowMap
  }: {
    relativeKey: string;
    relativeMap: Map<string, string>;
    sepId: string;
    wallId: string;
    edge: IWallEdge;
    wallWindowMap: Map<string, IWindowData[]>;
    layout?: EWallFaceLayout;
  }) {
    const sepWallData =
      this._separateWallStore.getSepData(sepId, false) || this._separateWallStore.getSepData(sepId, true);
    const relativeFaceDataId = relativeMap.get(relativeKey);

    if (!sepWallData) return;

    const isBaseVirtualWall = sepWallData.type === ESeparateWallType.BaseVirtualWall;

    // 处理半玻璃墙高度
    let height = sepWallData.height;
    let offGround = sepWallData.offGround;
    switch (layout) {
      case EWallFaceLayout.DOWN:
        height = clamp(HALF_GLASS_WALL_SEGMENT_HEIGHT, 0, sepWallData.height);
        break;
      case EWallFaceLayout.MIDDLE:
        offGround = clamp(
          offGround + HALF_GLASS_WALL_SEGMENT_HEIGHT,
          sepWallData.offGround,
          sepWallData.offGround + sepWallData.height
        );
        height = clamp(HALF_GLASS_WALL_SEGMENT_HEIGHT, 0, height - HALF_GLASS_WALL_SEGMENT_HEIGHT);
        break;
      case EWallFaceLayout.UP:
        offGround = clamp(
          offGround + HALF_GLASS_WALL_SEGMENT_HEIGHT * 2,
          sepWallData.offGround,
          sepWallData.offGround + sepWallData.height
        );
        height = clamp(height - HALF_GLASS_WALL_SEGMENT_HEIGHT * 2, 0, height - HALF_GLASS_WALL_SEGMENT_HEIGHT * 2);
        break;
    }

    // 开始更新/新增
    const relativeInfo = {
      wallId: edge.wallId,
      side: edge.side,
      sepId,
      sepSide: edge.sepSide,
      sepType: edge.sepType,
      startId: edge.startId,
      endId: edge.endId,
      regionId: edge.regionId,
      layout: layout ? layout : EWallFaceLayout.ALL
    };
    const baseData = {
      start: edge.start,
      end: edge.end,
      height,
      offGround,
      center: edge.center,
      clockwise: edge.clockwise
    };
    let holes: IFaceHolePoint[][] = [];
    // 实体墙两端点的边不挖洞
    if (
      edge.side !== EFaceSide.StartSide &&
      edge.side !== EFaceSide.EndSide &&
      (edge.sepSide === EFaceSide.MainLeftSide ||
        edge.sepSide === EFaceSide.MainRightSide ||
        edge.sepSide === EFaceSide.Single)
    ) {
      // 获取门窗数据
      const _wallId = isBaseVirtualWall ? wallId : sepId;
      let windows = wallWindowMap.get(_wallId);
      if (!windows) {
        windows = globalStores.modelStore.getWindowsByWallId(_wallId) || [];
        wallWindowMap.set(_wallId, windows);
      }

      holes = getFaceWindowHoles(windows, baseData);
    }
    if (relativeFaceDataId) {
      this._sepWallFaceDataMap.updateExtra(relativeFaceDataId, { baseData, holes, relativeInfo });
    } else {
      this.addSepWallFaceData({
        type: EFaceType.SepWallFace,
        baseDataType: baseData.center ? EFaceDataBaseType.ArcEdge : EFaceDataBaseType.Edge,
        baseData,
        relativeInfo: relativeInfo,
        holes,
        materialInfo: globalStores.getDefaultMaterial({
          type: EFaceType.SepWallFace,
          sepWallData,
          layout
        })
      });
    }

    return relativeFaceDataId;
  }

  refreshHoleFaceDatas() {
    const holeFaceDataMap = this.getHoleFaceMap();
    const removeHoleIds = new Set(this.getHoleFaceDatas().map((fd) => fd.id));

    for (const windowData of globalStores.modelStore.getWindowDatas()) {
      const relativeKey = windowData.id;

      const oldData = holeFaceDataMap.get(relativeKey);
      const baseData: IBaseDataHoleFace = {
        center: windowData.center,
        width: windowData.width,
        thickness: windowData.thickness,
        height: windowData.height,
        offGround: windowData.offGround,
        rotation: windowData.rotation
      };
      const relativeInfo = {
        dwId: windowData.id,
        wallId: '',
        sepId: ''
      };

      let wallHeight, wallOffGround;
      if (windowData.relativeWallInfo.wallType === EEntityType.SeparateWall) {
        relativeInfo.sepId = windowData.relativeWallInfo.relativeWallId;
        const separateWallData = globalStores.separateWallStore.getSepData(relativeInfo.sepId, false);
        const { height = 0, offGround = 0 } = separateWallData ?? {};

        baseData.arcCenter = separateWallData?.center;
        wallHeight = height;
        wallOffGround = offGround;
      } else {
        relativeInfo.wallId = windowData.relativeWallInfo.relativeWallId;
        const wallData = globalStores.wallStore.getWallData(relativeInfo.wallId);
        baseData.arcCenter = wallData?.baseData.center;
        wallHeight = wallData?.baseData.height ?? 0;
        wallOffGround = wallData?.baseData.offGround ?? 0;
      }

      if (wallHeight) {
        if (baseData.offGround >= wallHeight + wallOffGround) continue;
        if (baseData.offGround + baseData.height > wallHeight + wallOffGround) {
          baseData.height = wallHeight + wallOffGround - baseData.offGround;
        }
      }

      if (oldData) {
        removeHoleIds.delete(oldData.id);
        this._holeFaceDataMap.updateExtra(oldData.id, { baseData, holes: [], relativeInfo });
      } else {
        this.addHoleFaceData({
          type: EFaceType.HoleFace,
          baseDataType: EFaceDataBaseType.Hole,
          baseData,
          relativeInfo,
          holes: [],
          materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.HoleFace })
        });
      }
    }

    // 删除多余的孔洞face
    Array.from(removeHoleIds).forEach((id) => this.removeHoleFaceData(id));
  }

  private _refreshColumnEdgeFaceDadas({
    relativeKey,
    relativeMap,
    columnData,
    columnFaceSide,
    points
  }: {
    relativeKey: string;
    relativeMap: Map<string, string>;
    columnData: IColumnData;
    columnFaceSide: EFaceSide;
    points: IPoint2d[];
  }) {
    let relativeFaceDataId = relativeMap.get(relativeKey);
    const { height, offGround } = columnData;

    let start;
    let end;

    switch (columnFaceSide) {
      case EFaceSide.StartSide:
        start = points[0];
        end = points[1];
        break;
      case EFaceSide.MainLeftSide:
        start = points[1];
        end = points[2];
        break;
      case EFaceSide.EndSide:
        start = points[2];
        end = points[3];
        break;
      case EFaceSide.MainRightSide:
        start = points[3];
        end = points[0];
        break;
    }

    if (!start || !end) return;

    const relativeInfo = {
      columnId: columnData.id,
      columnSide: columnFaceSide
    };
    if (relativeFaceDataId) {
      this._columnFaceDataMap.updateExtra(relativeFaceDataId, {
        baseData: { start, end, height, offGround },
        holes: [],
        relativeInfo
      });
    } else {
      const fd = this.addColumnFaceData({
        id: getUniqueId(),
        type: EFaceType.ColumnFace,
        baseDataType: EFaceDataBaseType.Edge,
        baseData: { start, end, height, offGround },
        relativeInfo,
        holes: [],
        materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.ColumnFace })
      });
      relativeFaceDataId = fd.id;
    }

    return relativeFaceDataId;
  }
  private _refreshColumnContourFaceDadas({
    relativeKey,
    relativeMap,
    columnData,
    columnFaceSide,
    points
  }: {
    relativeKey: string;
    relativeMap: Map<string, string>;
    columnData: IColumnData;
    columnFaceSide: EFaceSide;
    points: IPoint2d[];
  }) {
    let relativeFaceDataId = relativeMap.get(relativeKey);
    const { height, offGround: _offGround } = columnData;
    const offGround = columnFaceSide === EFaceSide.TopSide ? _offGround + height : _offGround;
    const defaultMaterial = globalStores.getDefaultMaterial({ type: EFaceType.ColumnFace });

    const relativeInfo = {
      columnId: columnData.id,
      columnSide: columnFaceSide,
      side: columnFaceSide // 渲染依赖side而不是columnSide，待优化
    };
    if (relativeFaceDataId) {
      this._columnFaceDataMap.updateExtra(relativeFaceDataId, {
        baseData: { contour: points, offGround },
        holes: [],
        relativeInfo
      });
    } else {
      const fd = this.addColumnFaceData({
        type: EFaceType.ColumnFace,
        baseDataType: EFaceDataBaseType.Contour,
        baseData: { contour: points, offGround },
        relativeInfo,
        holes: [],
        materialInfo: defaultMaterial
      });
      relativeFaceDataId = fd.id;
    }

    return relativeFaceDataId;
  }

  refreshColumnFaceDadas() {
    const currentFaceMap = this.getColumnFaceDataMap();
    const columnDatas = this._structStore.getColumnDatas();
    const removeColumnFaceIds = new Set(this.getColumnFaceDatas().map((fd) => fd.id));

    for (const columnData of columnDatas) {
      const { rotation, center, height, offGround } = columnData;

      // --------------------- 圆柱处理 ---------------------
      if (columnData.type === EColumnType.cylinder) {
        const { radius } = columnData;
        const relativeKey = `${columnData.id}-${EFaceSide.CylinderSide}`;
        const relativeFaceDataId = currentFaceMap.get(relativeKey);
        const relativeInfo = {
          columnId: columnData.id,
          columnSide: EFaceSide.CylinderSide,
          side: EFaceSide.CylinderSide // TODO: 待优化: 渲染依赖side而不是columnSide
        };
        if (relativeFaceDataId) {
          removeColumnFaceIds.delete(relativeFaceDataId);
          this._columnFaceDataMap.updateExtra(relativeFaceDataId, {
            baseData: { center: center, radius, height: height, offGround: offGround },
            holes: [],
            relativeInfo
          });
        } else {
          this.addColumnFaceData({
            type: EFaceType.ColumnFace,
            baseDataType: EFaceDataBaseType.Cylinder,
            baseData: { center: center, radius, height: height, offGround: offGround },
            relativeInfo,
            holes: [],
            materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.ColumnFace })
          });
        }

        continue;
      }

      // --------------------- 方柱处理 ---------------------
      const { width = 0, thickness = 0 } = columnData;
      const points = [
        { x: width / 2 - COLUMN_RENDER_OFFSET, y: -thickness / 2 + COLUMN_RENDER_OFFSET }, // 右下
        { x: -width / 2 + COLUMN_RENDER_OFFSET, y: -thickness / 2 + COLUMN_RENDER_OFFSET }, // 左下
        { x: -width / 2 + COLUMN_RENDER_OFFSET, y: thickness / 2 - COLUMN_RENDER_OFFSET }, // 左上
        { x: width / 2 - COLUMN_RENDER_OFFSET, y: thickness / 2 - COLUMN_RENDER_OFFSET } // 右上
      ];
      // 计算旋转后的点
      points.forEach((point) => {
        const { x, y } = point;
        const { x: cx, y: cy } = center;
        const sin = Math.sin(rotation);
        const cos = Math.cos(rotation);
        point.x = cx + x * cos - y * sin;
        point.y = cy + x * sin + y * cos;
      });

      for (const edgeSide of COLUMN_EDGE_SIDES) {
        removeColumnFaceIds.delete(
          this._refreshColumnEdgeFaceDadas({
            relativeKey: `${columnData.id}-${edgeSide}`,
            relativeMap: currentFaceMap,
            columnData,
            columnFaceSide: edgeSide,
            points
          }) || ''
        );
      }

      for (const contourSide of COLUMN_CONTOUR_SIDES) {
        removeColumnFaceIds.delete(
          this._refreshColumnContourFaceDadas({
            relativeKey: `${columnData.id}-${contourSide}`,
            relativeMap: currentFaceMap,
            columnData,
            columnFaceSide: contourSide,
            points
          }) || ''
        );
      }
    }

    // 删除多余的柱面face
    Array.from(removeColumnFaceIds).forEach((id) => this.removeColumnFaceData(id));
  }

  refreshFaceDatas() {
    this.refreshWallFaceDatas();
    this.refreshSeparateWallFaceDatas();
    this.refreshColumnFaceDadas();
    this.refreshHoleFaceDatas();
  }

  // ======================== 其他 ========================
  getFaceData(faceId: string) {
    return (
      this._wallFaceDataMap.get(faceId) ||
      this._sepWallFaceDataMap.get(faceId) ||
      this._columnFaceDataMap.get(faceId) ||
      this._holeFaceDataMap.get(faceId)
    );
  }

  updateFaceMaterial(faceId: string, materialInfo: IMaterialInfo) {
    const oldFaceData = this.getFaceData(faceId);
    if (!oldFaceData) return;

    switch (oldFaceData.type) {
      case EFaceType.WallFace:
        this._wallFaceDataMap.updateMaterial(faceId, materialInfo);
        break;
      case EFaceType.SepWallFace:
        this._sepWallFaceDataMap.updateMaterial(faceId, materialInfo);
        break;
      case EFaceType.ColumnFace:
        this._columnFaceDataMap.updateMaterial(faceId, materialInfo);
        break;
      case EFaceType.HoleFace:
        this._holeFaceDataMap.updateMaterial(faceId, materialInfo);
        break;
    }
  }

  removeFaceData(faceId: string) {
    const fd = this.getFaceData(faceId);
    if (!fd) return;

    switch (fd.baseDataType) {
      case EFaceDataBaseType.Edge:
        this.removeWallFaceData(faceId);
        break;
      case EFaceDataBaseType.Contour:
        this.removeSepWallFaceData(faceId);
        break;
      case EFaceDataBaseType.Hole:
        this.removeHoleFaceData(faceId);
        break;
    }
  }

  clearAll() {
    this._wallFaceDataMap.clear();
    this._sepWallFaceDataMap.clear();
    this._columnFaceDataMap.clear();
    this._holeFaceDataMap.clear();
  }

  getDatas() {
    return {
      wallFaceDatas: this.getWallFaceDatas(),
      sepWallFaceDatas: this.getSepWallFaceDatas(),
      columnFaceDatas: this.getColumnFaceDatas(),
      holeFaceDatas: this.getHoleFaceDatas()
    };
  }

  coverDatas(data: {
    wallFaceDatas: IFaceData[];
    sepWallFaceDatas: IFaceData[];
    columnFaceDatas: IFaceData[];
    holeFaceDatas: IFaceData[];
  }) {
    this.clearAll();
    if (!data) return;

    try {
      if (data.wallFaceDatas) {
        for (const wallFaceData of data.wallFaceDatas) {
          this._wallFaceDataMap.add(wallFaceData);
        }
      }
      if (data.sepWallFaceDatas) {
        for (const sepWallFaceData of data.sepWallFaceDatas) {
          this._sepWallFaceDataMap.add(sepWallFaceData);
        }
      }
      if (data.columnFaceDatas) {
        for (const columnFaceData of data.columnFaceDatas) {
          this._columnFaceDataMap.add(columnFaceData);
        }
      }
      if (data.holeFaceDatas) {
        for (const holeFaceData of data.holeFaceDatas) {
          this._holeFaceDataMap.add(holeFaceData);
        }
      }
    } catch (error) {
      console.error('FaceStore coverDatas error', error);
      this.clearAll();
    }
  }
}
